"use client";

// First-party analytics tracker, mounted once in the root layout. Reports to
// /api/t: a page view per route change, a heartbeat while the tab is visible
// (powers the live "on site now" panel), and interaction events for the
// touchpoints the analytics dashboard breaks down — enquiry CTAs, document
// views/downloads, phone/email taps, and outbound social clicks.
//
// Privacy: the visitor id and session id are random values in the visitor's
// own browser storage. No IP, name or fingerprint is collected, and nothing
// is sent for /admin routes.
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { captureAttribution } from "@/lib/attribution";

function rid() {
  return Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
}

function ids(): { sid: string; vid: string } {
  let sid = "", vid = "";
  try {
    sid = sessionStorage.getItem("ts_sid") || "";
    if (!sid) { sid = rid(); sessionStorage.setItem("ts_sid", sid); }
    vid = localStorage.getItem("ts_vid") || "";
    if (!vid) { vid = rid(); localStorage.setItem("ts_vid", vid); }
  } catch {
    sid = sid || rid(); vid = vid || sid;
  }
  return { sid, vid };
}

function send(payload: Record<string, unknown>) {
  try {
    const body = JSON.stringify(payload);
    if (!navigator.sendBeacon?.("/api/t", new Blob([body], { type: "application/json" }))) {
      fetch("/api/t", { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" } }).catch(() => {});
    }
  } catch { /* analytics must never break the page */ }
}

function device(): "desktop" | "mobile" | "tablet" {
  const w = window.innerWidth;
  return w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop";
}

/** Which dashboard event (if any) a click on this element represents. */
function classify(el: HTMLElement): { name: string; meta: string } | null {
  const a = el.closest("a, button") as HTMLElement | null;
  if (!a) return null;
  const tracked = a.closest("[data-track]") as HTMLElement | null;
  if (tracked?.dataset.track) return { name: tracked.dataset.track, meta: a.textContent?.trim().slice(0, 80) ?? "" };
  const href = a.getAttribute("href") ?? "";
  if (href.startsWith("tel:")) return { name: "call_click", meta: href.slice(4) };
  if (href.startsWith("mailto:")) return { name: "email_click", meta: href.slice(7) };
  if (href.startsWith("/documents/")) return { name: "document_open", meta: href };
  if (/linkedin\.com|licdn\.com/.test(href)) return { name: "social_linkedin_click", meta: href.slice(0, 200) };
  if (/instagram\.com/.test(href)) return { name: "social_instagram_click", meta: href.slice(0, 200) };
  if (href.startsWith("#enquiry") || /enquire/i.test(a.textContent ?? "")) {
    return { name: "cta_enquire", meta: a.textContent?.trim().slice(0, 80) ?? "" };
  }
  return null;
}

export function SiteTracker() {
  const pathname = usePathname();
  const idsRef = useRef<{ sid: string; vid: string } | null>(null);

  // Page view on every route change.
  useEffect(() => {
    if (!pathname || pathname.startsWith("/admin")) return;
    if (!idsRef.current) idsRef.current = ids();
    // Record the campaign at arrival rather than at form submit, which is the
    // only way a lead from the third page of a visit still carries one. This
    // runs first so the ids it reads are already in storage, and it writes
    // nothing unless the URL actually carries campaign tags (lib/attribution).
    captureAttribution();
    const { sid, vid } = idsRef.current;
    send({ t: "pv", sid, vid, path: pathname, ref: document.referrer || undefined, d: device() });
  }, [pathname]);

  // Heartbeat while visible + interaction listeners, wired once.
  useEffect(() => {
    if (!idsRef.current) idsRef.current = ids();
    const { sid } = idsRef.current;

    const beat = () => {
      const p = window.location.pathname;
      if (document.visibilityState === "visible" && !p.startsWith("/admin")) {
        send({ t: "hb", sid, path: p });
      }
    };
    const timer = window.setInterval(beat, 25000);

    const onClick = (e: MouseEvent) => {
      const hit = classify(e.target as HTMLElement);
      if (hit) send({ t: "ev", sid, path: window.location.pathname, name: hit.name, meta: hit.meta });
    };
    const onSubmit = (e: Event) => {
      const form = e.target as HTMLFormElement;
      const label = form.getAttribute("aria-label") || form.id || "form";
      send({ t: "ev", sid, path: window.location.pathname, name: "form_submit", meta: label.slice(0, 80) });
    };
    document.addEventListener("click", onClick, { capture: true, passive: true });
    document.addEventListener("submit", onSubmit, true);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("submit", onSubmit, true);
    };
  }, []);

  return null;
}
